import { getCatalogMediaFeaturedImage, getDescription } from '@/Components/posts/utils/catalog';
import { ImageMediaPlaceHolder } from '@/assets/images';
import type { ZnglyVideo_Pagination_Node_Fragment$data } from '@/graphql/queries/ZnglyVideo.query';
import { MediaItemSizeEnum, type ZnglyVideo } from '@/graphql/types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Delete } from '@mui/icons-material';
import { Card, CardContent, CardMedia, Container, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';

export type CatalogListItemNode = Omit<ZnglyVideo_Pagination_Node_Fragment$data, ' $fragmentType'>;

export const SORTABLE_ITEM_DND_PREFIX = 'sortable-item-';

type CardItemBaseProps = {
  node: CatalogListItemNode;
  onRemove?: () => void;
};

type SortableCardProps = CardItemBaseProps & {
  sortableProps?: ReturnType<typeof useSortable>;
};

const SortableCard = ({ node, ...props }: SortableCardProps) => {
  const { description } = useMemo(() => getDescription(node as ZnglyVideo), [node?.znglyContent]);
  const featuredImage = useMemo(
    () => getCatalogMediaFeaturedImage(node?.znglyContent, MediaItemSizeEnum.Thumbnail) ?? ImageMediaPlaceHolder,
    [node?.znglyContent]
  );

  const sortableProps = useMemo(() => {
    if (!props?.sortableProps) {
      return {};
    }

    const { transform, transition, isDragging } = props.sortableProps;

    return {
      ref: props?.sortableProps?.setNodeRef,
      ...props?.sortableProps?.listeners,
      ...props?.sortableProps?.attributes,
      style: {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : undefined,
      },
    };
  }, [props?.sortableProps]);

  return (
    <Card
      {...sortableProps}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'nowrap',
        marginLeft: 'auto',
        marginRight: 'auto',
        borderRadius: '10px !important',
        boxShadow: '0px 2px 6px #0000004f !important',
        '&:hover': {
          '.ImageContainer img': {
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Container
        className="ImageContainer"
        sx={{
          width: 152,
          margin: 0,
          overflow: 'hidden',
          minWidth: 152,
          borderRight: '1px solid #ebebeb',
        }}
      >
        <CardMedia component="img" height="85" sx={{ width: 152 }} image={featuredImage} />
      </Container>
      <CardContent sx={{ width: '100%', overflow: 'hidden', padding: '16px' }}>
        <Typography component={'a'} sx={{ width: 'fit-content' }} target="_blank" rel="noreferrer">
          {node?.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {description}
        </Typography>
      </CardContent>
      <IconButton
        onClick={() => {
          props?.onRemove?.();
        }}
      >
        <Delete />
      </IconButton>
    </Card>
  );
};

const WithSortable = (props: CardItemBaseProps) => {
  const sortableProps = useSortable({
    id: SORTABLE_ITEM_DND_PREFIX + props?.node?.id,
    data: {
      node: props?.node,
    },
  });

  return <SortableCard {...props} sortableProps={sortableProps} />;
};

export const SortableCardItem = ({ useSortable, ...props }: CardItemBaseProps & { useSortable?: boolean }) => {
  if (useSortable === true) {
    return <WithSortable {...props} />;
  }

  return <SortableCard {...props} />;
};
